import React, { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { CheckCircleRounded, OpenInNewRounded, SendOutlined } from "@mui/icons-material";
import { QRCodeSVG } from "qrcode.react";
import Logo from "components/styled/Logo";
import { formatManualCode } from "services/healthRecordsShareService";

/**
 * Partner API demo. This page plays the part of a clinic's HMIS: it sends a patient in
 * through POST /api/v1/shared-records, then polls GET /api/v1/shared-records/:id until the
 * provider sends the finished visit back from ChartMind. Sent to the patient instead
 * (recipient "patient"), the record goes to their MasterChart and the poll stops once
 * they open it.
 *
 * Every call is a plain HTTPS request with the partner's own key, exactly what their
 * system would make. Nothing here goes through our app's auth.
 *
 * SANDBOX DEMO ONLY. The /api-demo route is registered only when isDevEnvironment() is
 * true (see routes/AppRoutes.jsx). Do not link to it from the app or expose it in production.
 */

// The dev server has no /api rewrite, so point local runs at sandbox.
const API_BASE =
  window.location.hostname === "localhost"
    ? "https://hlthdsk-sandbox-2cc23.web.app/api/v1"
    : "/api/v1";

const POLL_MS = 4000;

const sameHost = (url) => {
  try {
    const u = new URL(url);
    return `${window.location.origin}${u.pathname}${u.search}`;
  } catch {
    return url;
  }
};

// Only the key and the request id are kept, so a refresh mid-call doesn't lose the demo.
// No patient data is stored.
const SESSION_KEY = "apiDemo";
const readSession = () => {
  try {
    return JSON.parse(sessionStorage.getItem(SESSION_KEY) || "{}");
  } catch {
    return {};
  }
};
const writeSession = (value) => {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(value));
  } catch {
    // Storage blocked: the demo still works until a refresh.
  }
};

const STATUS_LABELS = {
  waiting: "Waiting for a provider",
  in_visit: "In visit",
  completed: "Visit completed",
  opened_by_patient: "Opened by the patient",
};

const lines = (value) =>
  String(value || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

const buildRequest = (v) => ({
  ...(v.recipient === "patient" && { recipient: "patient" }),
  externalPatientId: v.externalPatientId || undefined,
  patient: {
    name: v.name,
    dateOfBirth: v.dateOfBirth || undefined,
    sex: v.sex || undefined,
    weightKg: v.weightKg ? Number(v.weightKg) : undefined,
  },
  conditions: lines(v.conditions).map((condition) => ({ condition })),
  allergies: lines(v.allergies).map((allergen) => ({ allergen })),
  medications: lines(v.medications).map((line) => {
    const [name, dosage = "", frequency = ""] = line.split(",").map((s) => s.trim());
    return { name, dosage, frequency };
  }),
});

const JsonBlock = ({ value }) => (
  <Box
    component="pre"
    sx={{
      m: 0,
      p: 2,
      bgcolor: "action.hover",
      borderRadius: 1,
      overflow: "auto",
      maxHeight: 360,
      fontSize: 12,
      fontFamily: "monospace",
    }}
  >
    {JSON.stringify(value, null, 2)}
  </Box>
);

const StepTitle = ({ n, children }) => (
  <Typography variant="h6" sx={{ mb: 2 }}>
    {`${n}. ${children}`}
  </Typography>
);

const ApiDemoPage = () => {
  const saved = readSession();
  const [request, setRequest] = useState(null);
  const [created, setCreated] = useState(saved.created || null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  const [showResult, setShowResult] = useState(false);

  const { control, handleSubmit, getValues } = useForm({
    defaultValues: {
      apiKey: saved.apiKey || "",
      recipient: saved.created?.recipient || "provider",
      externalPatientId: "MRN-20417",
      name: "Grace Wanjiru",
      dateOfBirth: "1984-06-12",
      sex: "female",
      weightKg: "68",
      conditions: "Hypertension\nType 2 diabetes",
      allergies: "Penicillin",
      medications: "Metformin, 500 mg, twice daily\nAmlodipine, 5 mg, once daily",
    },
  });

  const onSubmit = async (values) => {
    const body = buildRequest(values);
    setRequest(body);
    setError("");
    setStatus(null);
    setSending(true);
    try {
      const res = await fetch(`${API_BASE}/shared-records`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${values.apiKey.trim()}`,
        },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `Request failed (${res.status})`);
      // Remember who it went to, so steps 2 and 3 survive a refresh with the right copy.
      const sent = { ...json, recipient: values.recipient };
      setCreated(sent);
      writeSession({ apiKey: values.apiKey.trim(), created: sent });
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const poll = useCallback(async () => {
    if (!created?.id) return;
    try {
      const res = await fetch(`${API_BASE}/shared-records/${created.id}`, {
        headers: { Authorization: `Bearer ${getValues("apiKey").trim()}` },
      });
      const json = await res.json();
      if (res.ok) setStatus(json);
    } catch {
      // Next poll will try again.
    }
  }, [created?.id, getValues]);

  const toPatient = created?.recipient === "patient";
  // The API links to the deployed app. Keep the demo on whatever host it is running
  // on, so a localhost run opens localhost rather than sandbox.
  const linkUrl = created?.url ? sameHost(created.url) : null;
  const done = status?.status === "completed" || status?.status === "opened_by_patient";
  useEffect(() => {
    if (!created?.id || done) return undefined;
    poll();
    const timer = setInterval(poll, POLL_MS);
    return () => clearInterval(timer);
  }, [created?.id, done, poll]);

  const reset = () => {
    setCreated(null);
    setStatus(null);
    setRequest(null);
    writeSession({ apiKey: getValues("apiKey").trim() });
  };

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "background.default", py: { xs: 3, md: 5 }, px: 2 }}>
      <Box sx={{ maxWidth: 1100, mx: "auto" }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
          <Logo />
        </Stack>
        <Typography variant="h4" sx={{ mb: 1 }}>
          Partner API demo
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 4, maxWidth: 720 }}>
          This page plays the part of your HMIS. It sends a patient to HealthDesk, waits while
          the provider sees them in ChartMind, then receives the finished visit back.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <StepTitle n={1}>Your system sends a patient</StepTitle>
                <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
                  <Stack spacing={2}>
                    <Controller
                      name="apiKey"
                      control={control}
                      rules={{ required: "Please paste your API key." }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label="API key"
                          type="password"
                          size="small"
                          error={Boolean(fieldState.error)}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                    <Controller
                      name="recipient"
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} select label="Send to" size="small">
                          <MenuItem value="provider">A provider, in ChartMind</MenuItem>
                          <MenuItem value="patient">The patient, in MasterChart</MenuItem>
                        </TextField>
                      )}
                    />
                    <Stack direction="row" spacing={2}>
                      <Controller
                        name="name"
                        control={control}
                        rules={{ required: "Name is required." }}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            label="Patient name"
                            size="small"
                            fullWidth
                            error={Boolean(fieldState.error)}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                      <Controller
                        name="externalPatientId"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} label="Your patient ID" size="small" fullWidth />
                        )}
                      />
                    </Stack>
                    <Stack direction="row" spacing={2}>
                      <Controller
                        name="dateOfBirth"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Date of birth"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      />
                      <Controller
                        name="sex"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} select label="Sex" size="small" fullWidth>
                            <MenuItem value="female">Female</MenuItem>
                            <MenuItem value="male">Male</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                          </TextField>
                        )}
                      />
                      <Controller
                        name="weightKg"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Weight (kg)"
                            type="number"
                            size="small"
                            fullWidth
                          />
                        )}
                      />
                    </Stack>
                    <Controller
                      name="conditions"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Conditions (one per line)"
                          size="small"
                          multiline
                          minRows={2}
                        />
                      )}
                    />
                    <Controller
                      name="allergies"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Allergies (one per line)"
                          size="small"
                          multiline
                          minRows={1}
                        />
                      )}
                    />
                    <Controller
                      name="medications"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Medications (name, dose, frequency)"
                          size="small"
                          multiline
                          minRows={2}
                        />
                      )}
                    />

                    {error && (
                      <Alert severity="error" onClose={() => setError("")}>
                        {error}
                      </Alert>
                    )}

                    <Stack direction="row" spacing={1} justifyContent="space-between">
                      <Button size="small" onClick={() => setShowRequest((v) => !v)}>
                        {showRequest ? "Hide request" : "Show request"}
                      </Button>
                      <LoadingButton
                        type="submit"
                        variant="contained"
                        startIcon={<SendOutlined />}
                        loading={sending}
                      >
                        Send patient
                      </LoadingButton>
                    </Stack>
                    <Collapse in={showRequest}>
                      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                        POST /api/v1/shared-records
                      </Typography>
                      <JsonBlock value={request || buildRequest(getValues())} />
                    </Collapse>
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              <Card>
                <CardContent>
                  <StepTitle n={2}>
                    {toPatient
                      ? "The patient adds it to MasterChart"
                      : "The provider opens it in ChartMind"}
                  </StepTitle>
                  {created ? (
                    <>
                      <Typography color="text.secondary" sx={{ mb: 2 }}>
                        {toPatient
                          ? "Your system texts, emails or prints the link or QR code for the patient. They sign in, review each item, and choose what to save."
                          : "Your system can link the provider straight in, or they can enter the code in ChartMind under Import shared record."}
                      </Typography>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={3}
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        sx={{ mb: 2 }}
                      >
                        {toPatient && linkUrl && (
                          <Box sx={{ p: 1.5, lineHeight: 0, bgcolor: "common.white", border: 1, borderColor: "divider", borderRadius: 2 }}>
                            <QRCodeSVG value={linkUrl} size={160} level="M" />
                          </Box>
                        )}
                        <Stack spacing={2} alignItems="flex-start">
                          <Typography variant="h3" sx={{ letterSpacing: 4 }}>
                            {formatManualCode(created.code)}
                          </Typography>
                          {linkUrl && (
                            <Button
                              variant="contained"
                              startIcon={<OpenInNewRounded />}
                              href={linkUrl}
                              target="_blank"
                              rel="noopener"
                            >
                              {toPatient ? "Open in MasterChart" : "Open in ChartMind"}
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {done ? (
                          <Chip
                            color="success"
                            icon={<CheckCircleRounded />}
                            label={STATUS_LABELS[status.status]}
                          />
                        ) : (
                          <>
                            <CircularProgress size={18} />
                            <Chip label={STATUS_LABELS[status?.status] || STATUS_LABELS.waiting} />
                          </>
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 2 }}>
                        {`Request id ${created.id}. Your system checks GET /api/v1/shared-records/${created.id}.`}
                      </Typography>
                    </>
                  ) : (
                    <Typography color="text.secondary">
                      A one-time code appears here once the patient is sent.
                    </Typography>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <StepTitle n={3}>
                    {toPatient
                      ? "Your system sees the patient opened it"
                      : "The visit comes back to your system"}
                  </StepTitle>
                  {toPatient ? (
                    done ? (
                      <Stack spacing={2}>
                        <Typography color="text.secondary">
                          The patient opened the record, and HealthDesk deleted its copy at that
                          moment. Your system learns that it was opened, not what the patient chose
                          to keep.
                        </Typography>
                        <Stack direction="row" justifyContent="space-between">
                          <Button size="small" onClick={() => setShowResult((v) => !v)}>
                            {showResult ? "Hide response" : "Show response"}
                          </Button>
                          <Button size="small" onClick={reset}>
                            Start over
                          </Button>
                        </Stack>
                        <Collapse in={showResult}>
                          <JsonBlock value={status} />
                        </Collapse>
                      </Stack>
                    ) : (
                      <Typography color="text.secondary">
                        When the patient opens the record, the status changes here.
                      </Typography>
                    )
                  ) : done ? (
                    <Stack spacing={2}>
                      {status.diagnoses?.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Diagnoses
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {status.diagnoses.map((d) => (
                              <Chip key={d.condition} label={d.condition} />
                            ))}
                          </Stack>
                        </Box>
                      )}
                      {status.treatments?.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Treatments
                          </Typography>
                          {status.treatments.map((t) => (
                            <Typography key={t.name} variant="body2">
                              {t.category ? `${t.name} (${t.category})` : t.name}
                            </Typography>
                          ))}
                        </Box>
                      )}
                      {status.note?.map((s) => (
                        <Box key={s.title}>
                          <Typography variant="subtitle2">{s.title}</Typography>
                          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                            {s.text}
                          </Typography>
                        </Box>
                      ))}
                      {status.dischargeInstructions?.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Discharge instructions
                          </Typography>
                          {status.dischargeInstructions.map((i) => (
                            <Typography key={i.title} variant="body2" sx={{ mb: 1 }}>
                              <strong>{i.title}.</strong> {i.text}
                            </Typography>
                          ))}
                        </Box>
                      )}
                      <Stack direction="row" justifyContent="space-between">
                        <Button size="small" onClick={() => setShowResult((v) => !v)}>
                          {showResult ? "Hide response" : "Show response"}
                        </Button>
                        <Button size="small" onClick={reset}>
                          Start over
                        </Button>
                      </Stack>
                      <Collapse in={showResult}>
                        <JsonBlock value={status} />
                      </Collapse>
                    </Stack>
                  ) : (
                    <Typography color="text.secondary">
                      When the provider sends the visit from ChartMind, the note, diagnoses,
                      treatments and discharge instructions appear here.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ApiDemoPage;
